import { useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCog, KeyRound } from "lucide-react";

export default function MyAccountPage() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = {};
      if (name.trim() && name.trim() !== user?.name) payload.name = name.trim();
      if (email.trim() && email.trim().toLowerCase() !== user?.email) payload.email = email.trim().toLowerCase();
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
      } else {
        const { data } = await api.patch("/auth/me", payload);
        setUser(data);
        toast.success("Profile updated");
      }
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setSavingProfile(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters");
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match");
    setSavingPw(true);
    try {
      await api.patch("/auth/me", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setSavingPw(false); }
  };

  return (
    <AppShell active="/account">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-carmel-gold mb-2">Personal</p>
        <h2 className="font-serif text-2xl md:text-4xl tracking-tight">My Account</h2>
        <p className="text-sm text-carmel-navy/60 mt-2">Update your display name, login email, and password.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={saveProfile} className="bg-white border border-carmel-navy/10 rounded-lg p-6 shadow-card">
          <div className="flex items-baseline gap-3 mb-5">
            <UserCog className="w-5 h-5 text-carmel-gold" />
            <h3 className="font-serif text-2xl tracking-tight">Profile & Login Email</h3>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">Display Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="me-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">Login Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="me-email-input" />
              <p className="text-[11px] text-carmel-navy/50 mt-1">This is the email you sign in with.</p>
            </div>
          </div>
          <Button type="submit" disabled={savingProfile} className="mt-6 bg-carmel-gold hover:bg-carmel-goldDark" data-testid="me-save-profile">
            Save changes
          </Button>
        </form>

        <form onSubmit={savePassword} className="bg-white border border-carmel-navy/10 rounded-lg p-6 shadow-card">
          <div className="flex items-baseline gap-3 mb-5">
            <KeyRound className="w-5 h-5 text-carmel-gold" />
            <h3 className="font-serif text-2xl tracking-tight">Change Password</h3>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">Current Password</Label>
              <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="mt-1" data-testid="me-current-password" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">New Password</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="mt-1" data-testid="me-new-password" />
              <p className="text-[11px] text-carmel-navy/50 mt-1">Minimum 6 characters.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-carmel-navy/60">Confirm New Password</Label>
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="mt-1" data-testid="me-confirm-password" />
            </div>
          </div>
          <Button type="submit" disabled={savingPw} className="mt-6 bg-carmel-navy hover:bg-carmel-navyMid" data-testid="me-save-password">
            Update password
          </Button>
        </form>
      </div>
    </AppShell>
  );
}
